// src/pages/Workers.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell.jsx";
import { apiFetchJson, getStoredUser } from "../api/api.js";

/** ---------------- helpers ---------------- */
function normalize(s) {
  return String(s ?? "").trim().toLowerCase();
}
function onlyDigitsPlus(s) {
  const raw = String(s ?? "").trim();
  if (!raw) return "";
  const leadPlus = raw.startsWith("+") ? "+" : "";
  const digits = raw.replace(/[^\d]/g, "");
  return leadPlus + digits;
}
function fmtDT(dt) {
  if (!dt) return "—";
  const d = new Date(String(dt));
  if (!Number.isFinite(d.getTime())) return String(dt);
  const pad2 = (n) => String(n).padStart(2, "0");
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function clampStr(s, max = 400) {
  const t = String(s ?? "").trim();
  return t.length > max ? t.slice(0, max) + "…" : t;
}
function statusToUa(st) {
  const v = String(st ?? "").toUpperCase();
  if (v === "PLANNED") return "Заплановано";
  if (v === "IN_PROGRESS") return "В роботі";
  if (v === "DONE") return "Завершено";
  if (v === "CANCELLED") return "Скасовано";
  return st ? String(st) : "—";
}

const SPEC_VALUES = ["ALL", "TRACTOR_DRIVER", "SPRAYER_OPERATOR", "SCOUT", "AGRONOMIST_ASSISTANT", "UNIVERSAL"];

/** ---------------- responsive hook ---------------- */
function useMediaQuery(q) {
  const [v, setV] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const fn = () => setV(m.matches);
    m.addEventListener("change", fn);
    return () => m.removeEventListener("change", fn);
  }, [q]);
  return v;
}

/** ---------------- theme via CSS variables ---------------- */
function pill(kind) {
  if (kind === "ok") {
    return {
      border: "1px solid rgba(40,163,91,0.24)",
      background: "rgba(40,163,91,0.10)",
      color: "var(--g)",
    };
  }
  if (kind === "warn") {
    return {
      border: "1px solid rgba(215,155,22,0.26)",
      background: "rgba(215,155,22,0.10)",
      color: "var(--warn)",
    };
  }
  if (kind === "bad") {
    return {
      border: "1px solid rgba(228,88,121,0.26)",
      background: "rgba(228,88,121,0.10)",
      color: "var(--danger)",
    };
  }
  return {
    border: "1px solid var(--border)",
    background: "var(--surface-soft)",
    color: "var(--muted)",
  };
}

const ui = {
  panel: {
    border: "1px solid var(--border)",
    background: "linear-gradient(180deg, var(--panel2), var(--panel))",
    borderRadius: 18,
    padding: 14,
    boxShadow: "var(--shadow)",
    backdropFilter: "blur(14px)",
  },

  title: {
    margin: 0,
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 0.2,
    color: "var(--text)",
  },

  sub: {
    marginTop: 6,
    fontSize: 12,
    color: "var(--muted)",
  },

  input: {
    width: "100%",
    minHeight: 48,
    borderRadius: 12,
    padding: "0 12px",
    fontSize: 16,
    border: "1px solid var(--border)",
    outline: "none",
    background: "var(--surface)",
    color: "var(--text)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18)",
  },

  textarea: {
    width: "100%",
    borderRadius: 12,
    padding: 12,
    fontSize: 16,
    border: "1px solid var(--border)",
    outline: "none",
    background: "var(--surface)",
    color: "var(--text)",
    resize: "vertical",
    minHeight: 110,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18)",
  },

  btn: {
    minHeight: 44,
    padding: "0 14px",
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "linear-gradient(180deg, var(--surface), var(--surface-soft))",
    color: "var(--text)",
    cursor: "pointer",
    fontWeight: 900,
    boxShadow: "var(--shadow-soft)",
  },

  btnPrimary: {
    minHeight: 44,
    padding: "0 14px",
    borderRadius: 12,
    border: "1px solid var(--primary-btn-border)",
    background: "var(--primary-btn-bg)",
    color: "var(--primary-btn-text)",
    cursor: "pointer",
    fontWeight: 950,
    boxShadow: "var(--primary-btn-shadow)",
  },

  iconBtn: {
    minHeight: 44,
    minWidth: 44,
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "linear-gradient(180deg, var(--surface), var(--surface-soft))",
    color: "var(--text)",
    cursor: "pointer",
    fontWeight: 900,
    display: "grid",
    placeItems: "center",
    boxShadow: "var(--shadow-soft)",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "var(--modal-overlay)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    padding: 14,
  },

  modal: {
    width: "min(980px, 96vw)",
    borderRadius: 18,
    overflow: "hidden",
    background: "var(--modal-bg)",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-strong)",
    backdropFilter: "blur(16px)",
  },

  modalHeader: {
    padding: 16,
    borderBottom: "1px solid var(--border)",
    background: "linear-gradient(180deg, var(--panel2), var(--panel))",
  },

  modalTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 950,
    letterSpacing: 0.2,
    color: "var(--text)",
  },

  modalSub: {
    marginTop: 6,
    fontSize: 12,
    color: "var(--muted)",
  },

  modalBody: { padding: 16 },

  label: { fontSize: 12, color: "var(--muted)" },
  hint: { fontSize: 12, color: "var(--muted)", lineHeight: 1.35 },

  hr: { height: 1, background: "var(--border)", margin: "14px 0" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  field: { display: "grid", gap: 6 },

  switchWrap: { display: "flex", alignItems: "center", gap: 10 },

  switch: (on) => ({
    width: 46,
    height: 26,
    borderRadius: 999,
    border: "1px solid var(--border)",
    background: on ? "rgba(40,163,91,0.22)" : "var(--surface-soft)",
    position: "relative",
    cursor: "pointer",
    transition: "all .15s ease",
  }),

  knob: (on) => ({
    width: 22,
    height: 22,
    borderRadius: 999,
    background: "var(--surface)",
    position: "absolute",
    top: 1.5,
    left: on ? 22 : 2,
    transition: "left .15s ease",
    boxShadow: "var(--shadow-soft)",
  }),
};

/** ---------------- Modal shell ---------------- */
function Modal({ open, title, subtitle, children, onClose, right }) {
  const { t } = useTranslation();
  const isMob = useMediaQuery("(max-width: 760px)");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const overlayStyle = isMob
    ? { ...ui.overlay, alignItems: "flex-end", padding: 0 }
    : ui.overlay;
  const modalStyle = isMob
    ? { ...ui.modal, width: "100vw", maxWidth: "100vw", maxHeight: "94vh", borderRadius: "18px 18px 0 0", display: "flex", flexDirection: "column" }
    : { ...ui.modal, display: "flex", flexDirection: "column", maxHeight: "90vh" };

  return (
    <div style={overlayStyle} onMouseDown={onClose} role="dialog" aria-modal="true">
      <div style={modalStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={ui.modalHeader}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={ui.modalTitle}>{title}</div>
              {subtitle ? <div style={ui.modalSub}>{subtitle}</div> : null}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {right ? right : null}
              <button onClick={onClose} style={ui.iconBtn} title={t("common.close")}>
                ✕
              </button>
            </div>
          </div>
        </div>
        <div style={{ ...ui.modalBody, overflowY: "auto", flex: 1, minHeight: 0 }}>{children}</div>
      </div>
    </div>
  );
}

/** ---------------- Custom select ---------------- */
function GreenSelect({ value, options, onChange, disabled, title }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const curVal = String(value ?? "");
  const curOpt = options.find((o) => String(o.value) === curVal) || options[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{
          ...ui.input,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          cursor: disabled ? "not-allowed" : "pointer",
          height: 40,
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={title}
      >
        <span style={{ fontWeight: 900, color: "var(--text)" }}>{curOpt?.label ?? "—"}</span>
        <span style={{ opacity: 0.75, color: "var(--muted)" }}>▾</span>
      </button>

      {open ? (
        <div
          role="listbox"
          style={{
            position: "absolute",
            zIndex: 40,
            left: 0,
            right: 0,
            top: 46,
            borderRadius: 14,
            border: "1px solid var(--border)",
            background: "var(--modal-bg)",
            boxShadow: "var(--shadow-strong)",
            overflow: "hidden",
            backdropFilter: "blur(16px)",
          }}
        >
          {options.map((opt) => {
            const active = String(opt.value) === curVal;
            return (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => {
                  onChange?.(opt.value);
                  setOpen(false);
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  border: "none",
                  background: active ? "var(--surface-active)" : "transparent",
                  color: active ? "var(--g)" : "var(--text)",
                  cursor: "pointer",
                  fontWeight: active ? 950 : 800,
                  borderBottom: "1px solid var(--border)",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/** ---------------- profile row ---------------- */
function InfoRow({ icon, label, children, right }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "28px 1fr auto",
        gap: 10,
        alignItems: "center",
        padding: "10px 12px",
        borderRadius: 14,
        border: "1px solid var(--border)",
        background: "var(--surface-soft)",
      }}
    >
      <div
        style={{
          height: 28,
          width: 28,
          borderRadius: 10,
          display: "grid",
          placeItems: "center",
          border: "1px solid var(--border)",
          background: "var(--surface-active)",
          color: "var(--g)",
          fontWeight: 950,
        }}
      >
        {icon}
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 950, color: "var(--text)", marginTop: 3, wordBreak: "break-word" }}>
          {children}
        </div>
      </div>

      {right ? <div style={{ display: "flex", alignItems: "center", gap: 8 }}>{right}</div> : <div />}
    </div>
  );
}

/** ---------------- backend errors ---------------- */
function parseFormErrors(errText) {
  const msg = String(errText ?? "").trim();
  const low = msg.toLowerCase();

  const out = { _global: "", phone: "", specialization: "", notes: "", active: "" };
  if (!msg) return out;

  if (
    low.includes("phone") ||
    low.includes("телефон") ||
    low.includes("letters") ||
    low.includes("invalid characters") ||
    low.includes("length")
  ) {
    out.phone =
      msg.includes("Phone must not contain letters")
        ? "Номер телефону не може містити букви."
        : msg.includes("Phone contains invalid characters")
        ? "У телефоні дозволені тільки цифри, пробіли, тире, дужки та + на початку."
        : msg.includes("Phone length")
        ? "Невірна довжина номера телефону (10–15 цифр)."
        : msg;
    return out;
  }

  out._global = msg;
  return out;
}

export default function Workers() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const SPEC = useMemo(() => SPEC_VALUES.map((v) => ({ value: v, label: t(`workers.spec.${v}`) })), [t]);
  const specLabel = (value) => {
    const v = String(value ?? "").trim().toUpperCase();
    return t(`workers.spec.${v}`, { defaultValue: "—" });
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState(null);
  const [activeOnly, setActiveOnly] = useState(false);
  const [q, setQ] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalKind, setModalKind] = useState("view");
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    phone: "",
    specialization: "UNIVERSAL",
    notes: "",
    active: true,
  });
  const [saving, setSaving] = useState(false);

  const [formErr, setFormErr] = useState({ _global: "", phone: "", specialization: "", notes: "", active: "" });

  const [allTasks, setAllTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksErr, setTasksErr] = useState("");

  function clearFormErrors() {
    setFormErr({ _global: "", phone: "", specialization: "", notes: "", active: "" });
  }

  function showToast(type, text) {
    setToast({ type, text });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 2600);
  }

  async function loadWorkers() {
    const u = getStoredUser();
    if (!u) {
      showToast("bad", t("workers.noSession"));
      navigate("/login");
      return;
    }

    setLoading(true);
    try {
      const r = await apiFetchJson(u, `/api/profile/workers?activeOnly=${activeOnly ? "true" : "false"}`, {}, navigate);

      if (!r.ok) {
        showToast("bad", r.error || t("common.error"));
        setItems([]);
        return;
      }

      setItems(Array.isArray(r.data) ? r.data : []);
    } finally {
      setLoading(false);
    }
  }

  async function loadAllTasks() {
    const u = getStoredUser();
    if (!u) return;

    setTasksLoading(true);
    setTasksErr("");
    try {
      const r = await apiFetchJson(u, `/api/tasks`, {}, navigate);
      if (!r.ok) {
        setAllTasks([]);
        setTasksErr(r.error || t("common.error"));
        return;
      }
      setAllTasks(Array.isArray(r.data) ? r.data : []);
    } finally {
      setTasksLoading(false);
    }
  }

  useEffect(() => {
    loadWorkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOnly]);

  function hasProfile(w) {
    return !!(w?.createdAt || w?.phone || w?.specialization || w?.notes);
  }

  const filtered = useMemo(() => {
    let base = activeOnly ? items.filter((w) => !!w?.active) : items;

    const qq = normalize(q);
    if (!qq) return base;

    return base.filter((w) => {
      const hay = [
        w?.userName,
        w?.phone,
        w?.specialization,
        specLabel(w?.specialization),
        w?.notes,
        String(w?.userId ?? ""),
        w?.active ? "active" : "inactive",
      ]
        .filter(Boolean)
        .map(normalize)
        .join(" | ");
      return hay.includes(qq);
    });
  }, [items, q, activeOnly]);

  function openView(worker) {
    clearFormErrors();
    setSelected(worker);
    setModalKind("view");
    setForm({
      phone: worker?.phone ?? "",
      specialization: String(worker?.specialization ?? "UNIVERSAL").toUpperCase(),
      notes: worker?.notes ?? "",
      active: worker?.active ?? true,
    });
    setModalOpen(true);
  }

  function openCreate(worker) {
    clearFormErrors();
    setSelected(worker);
    setModalKind("create");
    setForm({
      phone: worker?.phone ?? "",
      specialization: String(worker?.specialization ?? "UNIVERSAL").toUpperCase(),
      notes: worker?.notes ?? "",
      active: worker?.active ?? true,
    });
    setModalOpen(true);
  }

  function openEdit(worker) {
    clearFormErrors();
    setSelected(worker);
    setModalKind("edit");
    setForm({
      phone: worker?.phone ?? "",
      specialization: String(worker?.specialization ?? "UNIVERSAL").toUpperCase(),
      notes: worker?.notes ?? "",
      active: worker?.active ?? true,
    });
    setModalOpen(true);
  }

  function closeModal() {
    if (saving) return;
    setModalOpen(false);
    setSelected(null);
    clearFormErrors();
    setTasksErr("");
  }

  async function saveProfile() {
    const u = getStoredUser();
    if (!u) {
      showToast("bad", t("workers.sessionExpired"));
      navigate("/login");
      return;
    }
    if (!selected?.userId) return;

    clearFormErrors();

    const phone = String(form.phone ?? "").trim();
    const specialization = String(form.specialization ?? "").trim().toUpperCase();
    const notes = String(form.notes ?? "").trim();

    const payload = {
      userId: selected.userId,
      phone: phone || null,
      specialization: specialization || null,
      notes: notes || null,
      active: !!form.active,
    };

    setSaving(true);
    try {
      let r;
      if (modalKind === "create") {
        r = await apiFetchJson(u, `/api/profile/workers/profile`, { method: "POST", body: payload }, navigate);
      } else {
        r = await apiFetchJson(u, `/api/profile/workers/${selected.userId}/profile`, { method: "PUT", body: payload }, navigate);
      }

      if (!r.ok) {
        const errs = parseFormErrors(r.error || "Помилка збереження");
        setFormErr(errs);
        if (!errs.phone && !errs._global) showToast("bad", r.error || "Помилка збереження");
        return;
      }

      showToast("ok", modalKind === "create" ? t("workers.modal.createTitle") : t("workers.modal.editTitle"));
      closeModal();
      await loadWorkers();
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (!modalOpen) return;
    if (modalKind !== "view") return;

    if (!tasksLoading && allTasks.length === 0 && !tasksErr) {
      loadAllTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, modalKind]);

  const workerTasks = useMemo(() => {
    const uid = selected?.userId;
    if (!uid) return [];
    const userIdNum = Number(uid);

    return (allTasks || []).filter((t) => {
      const ids = Array.isArray(t?.workerIds) ? t.workerIds : [];
      return ids.includes(uid) || ids.includes(String(uid)) || ids.includes(userIdNum);
    });
  }, [allTasks, selected?.userId]);

  const headerRight = (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <button
        className="btn"
        onClick={() => setActiveOnly((v) => !v)}
        style={{ ...ui.btn, height: 34, borderRadius: 999, ...(activeOnly ? pill("ok") : pill("muted")) }}
        title={t("workers.filterActive")}
      >
        {activeOnly ? t("workers.activeOnly") : t("workers.all")}
      </button>

      <button className="btn" onClick={loadWorkers} style={{ ...ui.btn, height: 34 }}>
        {loading ? t("common.updating") : t("common.refresh")}
      </button>
    </div>
  );

  const modalTitle =
    modalKind === "view" ? t("workers.modal.viewTitle") : modalKind === "create" ? t("workers.modal.createTitle") : t("workers.modal.editTitle");

  const modalSubtitle = selected
    ? `${selected.userName ?? "Працівник"} • ID ${selected.userId}${selected.active ? "" : " • неактивний"}`
    : "";

  const callDigits = onlyDigitsPlus(form.phone);
  const canCall = !!callDigits;

  const phoneInputStyle = formErr.phone
    ? {
        ...ui.input,
        border: "1px solid rgba(228,88,121,0.34)",
        boxShadow: "0 0 0 3px rgba(228,88,121,0.10)",
      }
    : ui.input;

  return (
    <AppShell title={t("workers.title")} subtitle={t("workers.subtitle")} right={headerRight}>
      <style>{`
        @media (max-width: 760px) {
          .wk-card-actions { flex-direction: column !important; }
          .wk-card-actions .btn { width: 100% !important; }
          .wk-modal-footer { flex-direction: column-reverse !important; align-items: stretch !important; }
          .wk-modal-footer .btn { width: 100% !important; }
          .wk-search-input { width: 100% !important; }
        }
      `}</style>
      {toast ? (
        <div
          className="panel"
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 14,
            border: pill(toast.type).border,
            background: pill(toast.type).background,
            color: pill(toast.type).color,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 900 }}>{toast.text}</div>
        </div>
      ) : null}

      <div className="panel" style={{ ...ui.panel, marginTop: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div>
            <div style={ui.title}>{t("workers.title")}</div>
            <div style={ui.sub}>{loading ? t("common.loading") : `${t("workers.found")}: ${filtered.length}`}</div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ position: "relative" }}>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("workers.searchPlaceholder")}
                style={{ ...ui.input, height: 38, width: "min(340px, 78vw)", padding: "0 12px 0 38px" }}
              />
              <span style={{ position: "absolute", left: 12, top: 9, opacity: 0.65, color: "var(--muted)" }}>🔎</span>
            </div>
          </div>
        </div>
      </div>

      <div className="panel" style={{ ...ui.panel, marginTop: 14 }}>
        {loading ? (
          <div style={{ padding: 10, color: "var(--muted)" }}>{t("common.loading")}</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 10, color: "var(--muted)" }}>{t("workers.empty")}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
            {filtered.map((w) => {
              const profileOk = hasProfile(w);

              return (
                <div
                  key={w.userId}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: 18,
                    padding: 14,
                    background: "linear-gradient(180deg, var(--panel2), var(--panel))",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    minHeight: 178,
                    boxShadow: "var(--shadow-soft)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 950,
                          lineHeight: 1.2,
                          wordBreak: "break-word",
                          color: "var(--text)",
                        }}
                      >
                        {w?.userName || t("common.noName")}
                      </div>

                      <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 999, ...pill(w?.active ? "ok" : "bad") }}>
                          {w?.active ? t("workers.active") : t("workers.inactive")}
                        </span>

                        <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 999, ...pill(profileOk ? "ok" : "warn") }}>
                          {profileOk ? t("workers.profileFilled") : t("workers.profileNeeded")}
                        </span>

                        {w?.specialization ? (
                          <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 999, ...pill("muted") }}>
                            {specLabel(w.specialization)}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div style={{ fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>ID: {w?.userId}</div>
                  </div>

                  <div style={{ display: "grid", gap: 6, fontSize: 13, color: "var(--text)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <span style={{ color: "var(--muted)" }}>{t("workers.phone")}</span>
                      <span style={{ fontWeight: 900 }}>{w?.phone || "—"}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <span style={{ color: "var(--muted)" }}>{t("workers.specialization")}</span>
                      <span style={{ fontWeight: 900 }}>{specLabel(w?.specialization)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <span style={{ color: "var(--muted)" }}>{t("common.createdAt")}</span>
                      <span style={{ fontWeight: 900 }}>{fmtDT(w?.createdAt)}</span>
                    </div>
                  </div>

                  {w?.notes ? (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 13,
                        color: "var(--text)",
                        borderTop: "1px dashed var(--border)",
                        paddingTop: 10,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        opacity: 0.95,
                      }}
                    >
                      {clampStr(w.notes, 220)}
                    </div>
                  ) : null}

                  <div className="wk-card-actions" style={{ marginTop: "auto", display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button className="btn" onClick={() => openView(w)} style={ui.btn}>
                      {t("workers.viewProfile")}
                    </button>

                    {!profileOk ? (
                      <button className="btn primary" onClick={() => openCreate(w)} style={ui.btnPrimary}>
                        {t("common.create")}
                      </button>
                    ) : (
                      <button className="btn primary" onClick={() => openEdit(w)} style={ui.btnPrimary}>
                        {t("common.edit")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal open={modalOpen} title={modalTitle} subtitle={modalSubtitle} onClose={closeModal}>
        {modalKind === "view" ? (
          <div style={{ display: "grid", gap: 12 }}>
            <div
              style={{
                border: "1px solid var(--border)",
                borderRadius: 18,
                padding: 14,
                background: "linear-gradient(180deg, var(--panel2), var(--panel))",
                boxShadow: "var(--shadow-soft)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ display: "grid", gap: 8, minWidth: 280 }}>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>Статус</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 999, ...pill(selected?.active ? "ok" : "bad") }}>
                      {selected?.active ? t("workers.active") : t("workers.inactive")}
                    </span>
                    <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 999, ...pill(hasProfile(selected) ? "ok" : "warn") }}>
                      {hasProfile(selected) ? t("workers.profileFilled") : t("workers.profileEmpty")}
                    </span>
                    {selected?.specialization ? (
                      <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 999, ...pill("muted") }}>
                        {specLabel(selected.specialization)}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <button
                    className="btn primary"
                    onClick={() => (hasProfile(selected) ? openEdit(selected) : openCreate(selected))}
                    style={ui.btnPrimary}
                  >
                    {t("common.edit")}
                  </button>
                </div>
              </div>

              <div style={{ height: 1, background: "var(--border)", margin: "14px 0" }} />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <InfoRow
                    icon="👤"
                    label={t("workers.viewProfile")}
                    right={
                      <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 999, ...pill("muted") }}>
                        ID {selected?.userId ?? "—"}
                      </span>
                    }
                  >
                    {selected?.userName || t("common.noName")}
                  </InfoRow>
                </div>

                <InfoRow
                  icon="📞"
                  label={t("workers.phone")}
                  right={
                    selected?.phone ? (
                      <a
                        href={`tel:${onlyDigitsPlus(selected.phone)}`}
                        style={{
                          ...ui.btn,
                          height: 32,
                          padding: "0 10px",
                          borderRadius: 10,
                          textDecoration: "none",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {t("workers.call")}
                      </a>
                    ) : null
                  }
                >
                  {selected?.phone || "—"}
                </InfoRow>

                <InfoRow icon="🧑‍🌾" label={t("workers.specialization")}>
                  {specLabel(selected?.specialization)}
                </InfoRow>

                <InfoRow icon="🗓️" label={t("common.createdAt")}>
                  {fmtDT(selected?.createdAt)}
                </InfoRow>

                <InfoRow icon="⏱️" label={t("workers.lastActivity")}>
                  {fmtDT(selected?.lastActivity)}
                </InfoRow>
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>{t("workers.notes")}</div>
                <div
                  style={{
                    border: "1px dashed var(--border)",
                    borderRadius: 16,
                    padding: 12,
                    background: "var(--surface-soft)",
                    color: "var(--text)",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    lineHeight: 1.45,
                    minHeight: 52,
                  }}
                >
                  {selected?.notes ? selected.notes : <span style={{ color: "var(--muted)" }}>{t("workers.noNotes")}</span>}
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>
                    {t("workers.workerTasks", { count: workerTasks.length })}
                  </div>

                  <button
                    className="btn"
                    onClick={loadAllTasks}
                    style={{ ...ui.btn, height: 32, borderRadius: 10, opacity: tasksLoading ? 0.75 : 1 }}
                    disabled={tasksLoading}
                    title={t("workers.updateTasks")}
                  >
                    {tasksLoading ? t("common.updating") : t("workers.updateTasks")}
                  </button>
                </div>

                <div
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: 16,
                    padding: 12,
                    background: "var(--surface-soft)",
                  }}
                >
                  {tasksErr ? (
                    <div style={{ fontSize: 13, color: "var(--danger)", fontWeight: 800 }}>{tasksErr}</div>
                  ) : tasksLoading && allTasks.length === 0 ? (
                    <div style={{ fontSize: 13, color: "var(--muted)" }}>{t("workers.loadingTasks")}</div>
                  ) : workerTasks.length === 0 ? (
                    <div style={{ fontSize: 13, color: "var(--muted)" }}>{t("workers.noTasks")}</div>
                  ) : (
                    <div style={{ display: "grid", gap: 10 }}>
                      {workerTasks.map((t) => {
                        const id = t?.id;
                        const title = t?.title ?? "Задача";
                        const st = t?.taskStatus;
                        const planned = t?.plannedStartAt;

                        return (
                          <button
                            key={String(id)}
                            type="button"
                            onClick={() => {
                              if (!id) return;
                              const openTaskId = String(id);
                              closeModal();
                              navigate(`/tasks?openTaskId=${encodeURIComponent(openTaskId)}`, {
                                state: { openTaskId },
                              });
                            }}
                            style={{
                              width: "100%",
                              textAlign: "left",
                              border: "1px solid var(--border)",
                              background: "linear-gradient(180deg, var(--surface), var(--surface-soft))",
                              color: "var(--text)",
                              borderRadius: 14,
                              padding: "10px 12px",
                              cursor: "pointer",
                              display: "grid",
                              gap: 6,
                              boxShadow: "var(--shadow-soft)",
                            }}
                            title={t("workers.openTask")}
                          >
                            <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "baseline" }}>
                              <div style={{ fontSize: 14, fontWeight: 950, wordBreak: "break-word" }}>{title}</div>
                              <div style={{ fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>ID: {id}</div>
                            </div>

                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                              <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 999, ...pill("muted") }}>
                                {statusToUa(st)}
                              </span>
                              {planned ? (
                                <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 999, ...pill("muted") }}>
                                  {fmtDT(planned)}
                                </span>
                              ) : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {formErr._global || formErr.phone ? (
              <div
                style={{
                  padding: 12,
                  borderRadius: 16,
                  border: pill("bad").border,
                  background: pill("bad").background,
                  color: pill("bad").color,
                }}
              >
                <div style={{ fontWeight: 950, fontSize: 13 }}>{t("common.error")}</div>
                <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.35 }}>
                  {formErr.phone ? formErr.phone : formErr._global}
                </div>
              </div>
            ) : null}

            <div style={ui.grid2}>
              <div style={{ gridColumn: "1 / -1", ...ui.field }}>
                <label style={ui.label}>{t("workers.phone")}</label>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input
                    value={form.phone}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((s) => ({ ...s, phone: v }));
                      if (formErr.phone || formErr._global) {
                        setFormErr((p) => ({ ...p, phone: "", _global: "" }));
                      }
                    }}
                    placeholder="+380..."
                    style={phoneInputStyle}
                  />
                  <a
                    href={canCall ? `tel:${callDigits}` : undefined}
                    onClick={(e) => {
                      if (!canCall) e.preventDefault();
                    }}
                    style={{
                      ...ui.btn,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      opacity: canCall ? 1 : 0.5,
                      pointerEvents: canCall ? "auto" : "none",
                    }}
                    title={canCall ? "Подзвонити" : "Додай номер телефону"}
                  >
                    📞
                  </a>
                </div>

                {formErr.phone ? (
                  <div style={{ fontSize: 12, color: "var(--danger)", marginTop: 6, fontWeight: 800 }}>{formErr.phone}</div>
                ) : (
                  <div style={ui.hint}>{t("workers.phoneHint")}</div>
                )}
              </div>

              <div style={{ gridColumn: "1 / -1", ...ui.field }}>
                <label style={ui.label}>{t("workers.specialization")}</label>
                <GreenSelect
                  value={String(form.specialization ?? "UNIVERSAL").toUpperCase()}
                  options={SPEC.filter((x) => x.value !== "ALL")}
                  disabled={saving}
                  onChange={(v) => setForm((s) => ({ ...s, specialization: String(v).toUpperCase() }))}
                  title={t("workers.specialization")}
                />
              </div>

              <div style={{ gridColumn: "1 / -1", ...ui.field }}>
                <label style={ui.label}>{t("workers.notes")}</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                  placeholder={t("workers.notes")}
                  style={ui.textarea}
                />
              </div>
            </div>

            <div style={ui.hr} />

            <div className="wk-modal-footer" style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <div style={ui.switchWrap}>
                <div
                  role="switch"
                  aria-checked={!!form.active}
                  tabIndex={0}
                  onClick={() => setForm((s) => ({ ...s, active: !s.active }))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setForm((s) => ({ ...s, active: !s.active }));
                    }
                  }}
                  style={ui.switch(!!form.active)}
                  title="Активність профілю"
                >
                  <div style={ui.knob(!!form.active)} />
                </div>
                <div style={{ display: "grid", gap: 2 }}>
                  <div style={{ fontSize: 13, fontWeight: 950, color: "var(--text)" }}>{t("workers.activeProfile")}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>
                    {form.active ? t("workers.activeHint") : t("workers.inactiveHint")}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button className="btn" onClick={closeModal} disabled={saving} style={ui.btn}>
                  {t("common.cancel")}
                </button>
                <button
                  className="btn primary"
                  onClick={saveProfile}
                  disabled={saving}
                  style={saving ? { ...ui.btnPrimary, opacity: 0.7, cursor: "not-allowed" } : ui.btnPrimary}
                >
                  {saving ? t("common.saving") : t("common.save")}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </AppShell>
  );
}